import { Fragment, type ReactNode } from 'react';

/**
 * Small diagram kit for the docs guides.
 *
 * Every diagram is real HTML (ordered and unordered lists), not an image, so it reads
 * aloud correctly, scales with the text, follows the docs theme and stacks on a phone.
 * Wrap a diagram in <Figure> to give it a title and a caption.
 */

export type Tone = 'default' | 'accent' | 'warn' | 'danger' | 'muted';

const toneClass = (tone?: Tone) => (tone && tone !== 'default' ? ` dg-tone-${tone}` : '');

interface FigureProps {
  /** Short heading shown above the diagram. */
  title: string;
  /** One sentence under the diagram saying what to take from it. */
  caption?: string;
  children: ReactNode;
}

export function Figure({ title, caption, children }: FigureProps) {
  return (
    <figure className="dg-figure">
      <div className="dg-figure-title">{title}</div>
      <div className="dg-figure-body">{children}</div>
      {caption ? <figcaption className="dg-figure-caption">{caption}</figcaption> : null}
    </figure>
  );
}

export interface FlowNode {
  title: string;
  text?: string;
  tone?: Tone;
}

/** One step in a flow: a node, or a fork where the path splits into alternatives. */
export type FlowStep = FlowNode | { fork: FlowNode[] };

const isFork = (s: FlowStep): s is { fork: FlowNode[] } => 'fork' in s;

function NodeBox({ node }: { node: FlowNode }) {
  return (
    <div className={`dg-node${toneClass(node.tone)}`}>
      <div className="dg-node-title">{node.title}</div>
      {node.text ? <div className="dg-node-text">{node.text}</div> : null}
    </div>
  );
}

/** A left-to-right sequence of steps (top-to-bottom on a phone). */
export function Flow({ steps }: { steps: FlowStep[] }) {
  return (
    <ol className="dg-flow">
      {steps.map((step, i) => (
        <li className="dg-flow-step" key={i}>
          {isFork(step) ? (
            <div className="dg-fork">
              {step.fork.map((n, j) => (
                <Fragment key={j}>
                  {j > 0 ? <div className="dg-or">or</div> : null}
                  <NodeBox node={n} />
                </Fragment>
              ))}
            </div>
          ) : (
            <NodeBox node={step} />
          )}
        </li>
      ))}
    </ol>
  );
}

export interface TimelineItem {
  /** When it happens, for example "03:00 daily" or "Day 1". */
  when: string;
  title: string;
  text?: string;
  tone?: Tone;
}

/** Events along a line. Vertical on a phone. */
export function Timeline({ items }: { items: TimelineItem[] }) {
  return (
    <ol className="dg-timeline">
      {items.map((item, i) => (
        <li className={`dg-tl-item${toneClass(item.tone)}`} key={i}>
          <div className="dg-tl-when">{item.when}</div>
          <div className="dg-tl-dot" aria-hidden="true" />
          <div className="dg-tl-title">{item.title}</div>
          {item.text ? <div className="dg-tl-text">{item.text}</div> : null}
        </li>
      ))}
    </ol>
  );
}

export interface LayerRow {
  title: string;
  note?: string;
  /** Short labels shown as chips on the row. */
  chips?: string[];
  tone?: Tone;
}

/** Rows stacked in priority order. The first row wins. */
export function Layers({ rows, orderLabel }: { rows: LayerRow[]; orderLabel?: string }) {
  return (
    <div className="dg-layers-wrap">
      {orderLabel ? <div className="dg-layers-order">{orderLabel}</div> : null}
      <ol className="dg-layers">
        {rows.map((row, i) => (
          <li className={`dg-layer${toneClass(row.tone)}`} key={i}>
            <span className="dg-layer-rank" aria-hidden="true">
              {i + 1}
            </span>
            <div className="dg-layer-main">
              <div className="dg-layer-title">{row.title}</div>
              {row.note ? <div className="dg-layer-note">{row.note}</div> : null}
            </div>
            {row.chips?.length ? (
              <div className="dg-chips">
                {row.chips.map((c) => (
                  <span className="dg-chip" key={`${i}-${c}`}>
                    {c}
                  </span>
                ))}
              </div>
            ) : null}
          </li>
        ))}
      </ol>
    </div>
  );
}

export interface SplitPart {
  label: string;
  /** Relative size. Only the proportions matter. */
  value: number;
  /** What the amount is, shown under the label, for example "100.00". */
  amount?: string;
  tone?: Tone;
}

/** A single bar divided in proportion, with a legend that carries the numbers. */
export function Split({ parts, total }: { parts: SplitPart[]; total?: string }) {
  const sum = parts.reduce((acc, p) => acc + p.value, 0) || 1;
  return (
    <div className="dg-split">
      <div className="dg-split-bar" aria-hidden="true">
        {parts.map((p, i) => (
          <div
            className={`dg-split-seg${toneClass(p.tone)}`}
            style={{ flexGrow: p.value, flexBasis: `${(p.value / sum) * 100}%` }}
            key={i}
          />
        ))}
      </div>
      <ul className="dg-split-legend">
        {parts.map((p, i) => (
          <li className={`dg-split-item${toneClass(p.tone)}`} key={i}>
            <span className="dg-split-swatch" aria-hidden="true" />
            <span className="dg-split-label">{p.label}</span>
            {p.amount ? <span className="dg-split-amount">{p.amount}</span> : null}
          </li>
        ))}
        {total ? (
          <li className="dg-split-item dg-split-total">
            <span className="dg-split-label">Total</span>
            <span className="dg-split-amount">{total}</span>
          </li>
        ) : null}
      </ul>
    </div>
  );
}
